import * as z from 'zod';
import { AgendamentoStatus } from '@/constants/agendamentos';
import { FORMAS_PAGAMENTO_AGENDAMENTO } from '@/constants/pagamentoAgendamento';

const formasPagamento = FORMAS_PAGAMENTO_AGENDAMENTO.map(opcao => opcao.value) as [string, ...string[]];

/**
 * Tipos e schemas relacionados aos agendamentos
 * 
 * Este arquivo centraliza as definições de tipos para agendamentos,
 * incluindo validações com Zod e enums de status.
 * 
 * O enum `AgendamentoStatus` é importado de `@/constants/agendamentos`
 * para manter uma única fonte da verdade no sistema.
 */

// Schema de validação com Zod para formulário de agendamento
export const agendamentoFormSchema = z.object({
  cliente_id: z.string().trim().default(''),
  titulo: z.string().trim().min(1, { message: 'Informe o serviço ou o título do agendamento.' }),
  data_inicio: z.date({ required_error: 'Data de início é obrigatória.' }),
  data_fim: z.date({ required_error: 'Data de fim é obrigatória.' }),
  valor: z.number().min(0, { message: 'O valor não pode ser negativo.' }).default(0),
  status: z.nativeEnum(AgendamentoStatus, { required_error: 'Status é obrigatório.' }),
  forma_pagamento: z.union([z.enum(formasPagamento), z.literal('')]).optional(),
  parcelas: z.number().int().min(1, { message: 'Informe pelo menos 1 parcela.' }).default(1),
  pago_em: z.date().optional(),
  descricao: z.string().trim().optional(),
  clinica_id: z.string().min(1, { message: 'Clínica não identificada.' }),
  usuario_id: z.string().min(1, { message: 'Usuário não identificado.' }),
  novo_cliente_nome: z.string().trim().optional(),
  novo_cliente_telefone: z.string().trim().optional(),
}).superRefine((data, contexto) => {
  // Exige um cliente existente ou os dados mínimos de um novo contato.
  // Motivo: todo agendamento precisa apontar para um lead real para alimentar o histórico e o dashboard.
  if (!data.cliente_id) {
    if (!data.novo_cliente_nome) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['novo_cliente_nome'],
        message: 'Informe o nome do novo cliente.',
      });
    }

    const telefoneComApenasNumeros = data.novo_cliente_telefone?.replace(/\D/g, '') ?? '';
    if (telefoneComApenasNumeros.length < 10) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['novo_cliente_telefone'],
        message: 'Informe um telefone com DDD.',
      });
    }
  }

  // Impede horário invertido ou sem duração, que quebraria a posição do evento na agenda.
  if (data.data_fim <= data.data_inicio) {
    contexto.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['data_fim'],
      message: 'A data de fim deve ser posterior ao início.',
    });
  }

  // Sem forma o dashboard não consegue dizer como o dinheiro entrou.
  if (data.status === AgendamentoStatus.PAGO && !data.forma_pagamento) {
    contexto.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['forma_pagamento'],
      message: 'Escolha como o paciente pagou.',
    });
  }
});

export type AgendamentoFormData = z.infer<typeof agendamentoFormSchema>;

// Função utilitária para converter data para ISO string
export const formatarDataParaISO = (data: Date): string => {
  return data.toISOString();
};
